/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState, useEffect } from 'react';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import { func, bool } from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/styles';
import { Drawer as MuiDrawer } from '@material-ui/core';
import { getSSRDeviceType } from '../../../state/ducks/App/App-Selectors';
import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getProfileInfo } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    paper: {
        width: '450px',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '90vh',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            borderBottomLeftRadius: '0px',
            borderBottomRightRadius: '0px',
        },
    },
    backdrop: {
        zIndex: '9999 !important',
        '& .MuiBackdrop-root': {
            opacity: '0.25 !important',
        },
    },
    container: {
        width: 'auto',
        height: '100vh',
        boxShadow: 'none',
        border: 'none',
    },
}));

const RemindersDrawerContainer = ({ open, onClose }) => {
    // Styles
    const theme = useTheme();
    const classes = useStyles();

    // Selectors
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const userInfo = useSelector(getProfileInfo);
    const brand = useSelector(getBrandName) || '1800flowers';
    const isUsingAppDirectOriginUrl = useSelector(getFeatureFlag('is-using-app-direct-origin-url'));

    // State & Refs
    const iframeRef = useRef(null);
    const [isIframeLoaded, setIsIframeLoaded] = useState(true);

    let trustedOrigin = `${mbpUtil.getEnv('APP_MBP_ENGAGEMENT_HOST')}`;
    if (isUsingAppDirectOriginUrl) {
        trustedOrigin = `https://${mbpUtil.getEnv('APP_DIRECT_ORIGIN_URL')}`;
    }

    const isMobile = ssrDeviceType === 'mobile';

    useEffect(() => {
        /* Handle messages event types:
            - REQUEST_CLOSE_DRAWER

            - REQUEST_USER_PROFILE
            - RESPONSE_USER_PROFILE

            - REQUEST_TOKEN_REFRESH
            - RESPONSE_TOKEN_REFRESH

            Optional:
            - REQUEST_THEME
            - RESPONSE_THEME
        */
        const handleiFrameMessage = (event) => {
            try {
                const eventType = event.data?.type || '';

                // Registered User - send user profile to iFrame
                if (eventType === 'REQUEST_USER_PROFILE') {
                    const auth = JSON.parse(JSON.parse(sessionStorage?.getItem('persist:member'))?.auth);
                    const accessToken = auth?.accessToken || '';
                    event.source.postMessage(
                        {
                            type: 'RESPONSE_USER_PROFILE',
                            payload: { ...userInfo, accessToken },
                        },
                        trustedOrigin,
                    );
                // Close Drawer
                } else if (eventType === 'REQUEST_CLOSE_DRAWER') {
                    onClose();
                // Send Fresh Token to iFrame
                } else if (eventType === 'REQUEST_TOKEN_REFRESH') {
                    // Re-validate token
                    getAccessTokenSafely()
                        .then((token) => {
                            event.source.postMessage(
                                {
                                    type: 'RESPONSE_TOKEN_REFRESH',
                                    payload: token,
                                },
                                trustedOrigin,
                            );
                        })
                        .catch((error) => {
                            mbpLogger.logError({
                                appName: process.env.npm_package_name,
                                jsError: error,
                                module: 'RemindersDrawerContainer.js',
                                function: 'handleiFrameMessage',
                                message: 'Failed to refresh token on iFrame request',
                            });
                        });
                // Optional - Allow iFrame to request brand theme from origin
                } else if (eventType === 'REQUEST_THEME') {
                    event.source.postMessage(
                        {
                            type: 'RESPONSE_THEME',
                            payload: JSON.stringify(theme),
                        },
                        trustedOrigin,
                    );
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    jsError: error,
                    module: 'RemindersDrawerContainer.js',
                    function: 'handleiFrameMessage',
                    message: 'handleiFrameMessage could not post value to iFrame',
                });
            }
        };

        if (typeof window !== 'undefined' && isIframeLoaded) {
            window.addEventListener('message', handleiFrameMessage);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('message', handleiFrameMessage);
            }
        };
    }, [isIframeLoaded, trustedOrigin, userInfo]);

    return isIframeLoaded ? (
        <MuiDrawer
            open
            onClose={onClose}
            disableScrollLock
            disableRestoreFocus
            anchor={ssrDeviceType === 'mobile' ? 'bottom' : 'right'}
            classes={{
                paper: classes.paper,
                root: classes.backdrop,
            }}
        >
            <iframe
                ref={iframeRef}
                id="engagement-occasions"
                title="engagement-occasions"
                className={classes.container}
                src={`${trustedOrigin}/mbp-engagement-ui/reminders-container`}
                onLoad={() => setIsIframeLoaded(true)}
                onError={() => setIsIframeLoaded(false)}
            />
        </MuiDrawer>
    ) : null;
};

RemindersDrawerContainer.propTypes = {
    open: bool.isRequired,
    onClose: func.isRequired,
};

export default RemindersDrawerContainer;
